<template>
    <DataTable
        ref="elementTable"
        :rowClass="elementTableRowClass"
        :value="sortedProcessElements"
        selectionMode="single"
        rowGroupMode="subheader"
        scrollable
        scrollHeight="300px"
        v-bind="$attrs"
        :pt="{
            bodyRow: {
                onmouseenter: (e: MouseEvent) => {
                    highlightElement(e);
                },
                onmouseleave: () => {
                    highlightElementId = null;
                },
            },
        }"
        @row-click="
            (event: DataTableRowClickEvent) => {
                const rowLink = `process-element-link-${event.data.identity}`;
                elementTable.$el.querySelector(`#${rowLink}`)?.click();
            }
        "
    >
        <template #empty>
            <div>No elements</div>
        </template>

        <template #header v-if="header">
            <h3>{{ header }}</h3>
        </template>

        <Column field="type" header="" class="type-col">
            <template #body="{ data }: { data: ProcessElement }">
                <i :class="processElementIcon(data)" :title="data.type" />
            </template>
        </Column>
        <Column field="name" header="Name" class="name-col">
            <template #body="{ data }: { data: ProcessElement }">
                <RouterLink
                    :id="`process-element-link-${data.identity}`"
                    class="process-element-link"
                    :to="routerLink(data)"
                >
                    <div :data-element-id="data.bpmn_id" class="task-row">
                        {{ data.name || data.bpmn_id }}
                    </div>
                </RouterLink>
            </template>
        </Column>

        <Column field="short_description" header="Description">
            <template #body="slotProps">
                <template v-if="slotProps.data.short_description">
                    {{ slotProps.data.short_description }}
                </template>
                <p v-else v-tooltip="'A description is not (yet) available'">
                    ...
                </p>
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import { ProcessElementType } from "@/lib/openapi/gen";
import type { ProcessElement } from "@/stores/process-elements-store";
import type { DataTableRowClickEvent } from "primevue/datatable";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

const highlightElementId = defineModel<string | null>();
const route = useRoute();

const elementTable = ref();

const props = defineProps<{
    processElements: ProcessElement[] | undefined;
    header?: string | undefined;
}>();

const sortedProcessElements = computed(() => {
    return props.processElements?.toSorted((a, b) => {
        const typeOrder = {
            [ProcessElementType.TASK]: 1,
            [ProcessElementType.EVENT]: 2,
            [ProcessElementType.GATEWAY]: 3,
            [ProcessElementType.FLOW]: 4,
        };
        return (typeOrder[a.type] || 999) - (typeOrder[b.type] || 999);
    });
});

const elementTableRowClass = (data: ProcessElement) => {
    return {
        "highlight": highlightElementId.value === data.bpmn_id,
    };
};

const highlightElement = (event: MouseEvent) => {
    const elementId = (event.target as HTMLElement)
        ?.closest("tr")
        ?.querySelector("div")
        ?.getAttribute("data-element-id");
    if (elementId) {
        highlightElementId.value = elementId;
    }
};

const processElementIcon = (element: ProcessElement) => {
    switch (element?.type) {
        case ProcessElementType.TASK:
            return "pi pi-check-square";
        case ProcessElementType.EVENT:
            return "pi pi-stop-circle";
        case ProcessElementType.GATEWAY:
            return "pi pi-directions";
        case ProcessElementType.FLOW:
            return "pi pi-window-minimize";
        default:
            return "";
    }
};

const routerLink = (element: ProcessElement) => {
    switch (element?.type) {
        case ProcessElementType.TASK:
            return {
                name: "taskPage",
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    taskId: element.identity,
                },
            };
        case ProcessElementType.EVENT:
            return {
                name: "eventPage",
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    eventId: element.identity,
                },
            };
        case ProcessElementType.GATEWAY:
            return {
                name: "gatewayPage",
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    gatewayId: element.identity,
                },
            };
        case ProcessElementType.FLOW:
            return {
                name: "flowPage",
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    flowId: element.identity,
                },
            };
        default:
            return {};
    }
};
</script>

<style scoped lang="css">
.process-element-link {
    text-decoration: none;
}

div.task-row {
    display: flex;
    align-items: center;

    span {
        padding-right: 1rem;
    }
}

:deep(.p-datatable-table) tr.highlight {
    background: var(--p-datatable-row-hover-background);
    color: var(--p-datatable-row-hover-color);
}

:deep(td.type-col) {
    width: 25px;
}
:deep(td.name-col) {
    width: 30rem;
}
</style>
